import React from "react";
import { useNavigate } from "react-router-dom";
import Icon from "./Icon";
import { Chip } from "./primitives";
import { useAuth } from "../context/AuthContext";
import { useData } from "../context/DataContext";
import { useUnread } from "../context/UnreadContext";

/** Scroll container every routed page renders inside (mockup `.scroll`). Chats may skip it for its own panes. */
export function Page({ children, className = "" }) {
  return <div className={`scroll ${className}`.trim()}>{children}</div>;
}

/**
 * Gradient header band for top-level pages (DynamicHeader on mobile).
 * `right` is extra chips rendered before the Manage Club chip (shown to club leaders), the Super Admin
 * chip (super admins; the only way in below 820px, where the sidebar is hidden) and the bell
 * (red dot when there are unread notifications, or while the unread listener is failing, since the
 * count is then unknown rather than 0). `unread` overrides the UnreadContext value.
 */
export function Band({ title, sub, right, unread }) {
  const navigate = useNavigate();
  const { leadsAnyClub } = useData();
  const { isSuperAdmin } = useAuth();
  const ctx = useUnread();
  const count = unread ?? ctx.notifications;
  const unknown = unread == null && ctx.unreadError;
  return (
    <header className="band">
      <div className="band-in">
        <div className="grow">
          <h1>{title}</h1>
          {sub ? <div className="sub">{sub}</div> : null}
        </div>
        {right}
        {leadsAnyClub && (
          <Chip icon="shield" data-testid="header.manageClub" onClick={() => navigate("/manage")}>Manage Club</Chip>
        )}
        {isSuperAdmin && (
          <Chip iconOnly aria-label="Super Admin" title="Super Admin" data-testid="header.superAdmin" onClick={() => navigate("/super")}>
            <Icon name="star" />
          </Chip>
        )}
        <Chip iconOnly dot={count > 0 || unknown} aria-label={unknown ? "Notifications (unread count unavailable)" : "Notifications"} data-testid="header.notifications" onClick={() => navigate("/notifications")}>
          <Icon name="bell" />
        </Chip>
      </div>
    </header>
  );
}

/**
 * Back header for detail pages: navigate(-1), falling back to /clubs when there is no history.
 * The back chip's test id is `${testId}.back` (mobile: eventDetails.back, settings.back).
 */
export function BackBar({ title, right, fallback = "/clubs", locked = false, testId = "backBar" }) {
  const navigate = useNavigate();
  const back = () => {
    if (window.history.state && window.history.state.idx > 0) navigate(-1);
    else navigate(fallback, { replace: true });
  };
  return (
    <header className="band" style={{ paddingBlock: "18px 50px" }}>
      <div className="band-in">
        <Chip iconOnly glass aria-label="Back" data-testid={`${testId}.back`} disabled={locked} onClick={back}><Icon name="left" /></Chip>
        <h1 className="grow" style={{ fontSize: 22 }}>{title}</h1>
        {right}
      </div>
    </header>
  );
}
